import { useState } from "react";
import { T, UI, BTN } from "../constants/tokens";
import { useHoldingMarks, isAnswered } from "../utils/holdingSegments";
import SegmentPicker from "./SegmentPicker";
import Icon from "./Icon";
import LTR from "./LTR";

// ─── "יש החזקות שעוד לא סימנת" ───────────────────────────────────────────────
// One line at the top of the home screen for a portfolio built before the
// marking layer existed. It counts the holdings with no answer yet — marked
// or "not sure" both count as answered — and offers to walk through them, one
// question each, in portfolio order.
//
// Skippable: the X snoozes the banner for 30 days on this browser (a
// timestamp in localStorage), after which it returns quietly if holdings are
// still unanswered — "for good" fits a prompt with no recurring value, and a
// CSV import or a slip of the thumb both argue for a return, spaced out. The
// row chip in the portfolio ("סמן תחום למעקב") is the everyday way in either
// way. localStorage is the right place for the snooze — dismissing a prompt is
// a per-device convenience, not a fact about the account — and every access is
// wrapped, so a browser that refuses storage simply shows the banner again.
//
// Not shown to guests: the demo portfolio is reseeded on each guest load, so
// there is nothing durable to mark.
const DISMISS_KEY = "si_mark_invite_dismissed";
const SNOOZE_MS   = 30 * 24 * 60 * 60 * 1000;

// Snoozed while the stored timestamp is less than 30 days old. The first
// version stored "1" (forever); that value parses to NaN and reads as
// not-snoozed, so an old dismissal simply expires with this change.
function readDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS;
  } catch { return false; }
}
function writeDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* shows again next time */ }
}

export default function MarkInviteBanner({ portfolio = [], isGuest = false, style }) {
  const { marks, loaded } = useHoldingMarks();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [queue, setQueue] = useState(null);   // symbols still to ask, while walking through

  if (isGuest || dismissed || !loaded) return null;

  const pending = portfolio.map(p => p.symbol).filter(sym => sym && !isAnswered(marks, sym));
  if (!pending.length && !queue) return null;

  const start = () => setQueue(pending);
  // After each answer (or a close), move on; the store already reflects the
  // answer, so a symbol answered elsewhere meanwhile is not asked again.
  const next = () => {
    setQueue(q => {
      const rest = (q || []).slice(1).filter(sym => !isAnswered(marks, sym));
      return rest.length ? rest : null;
    });
  };
  const stop = () => setQueue(null);

  return (
    <>
      {pending.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`,
          borderRadius: T.radius.lg, padding: "10px 14px", ...style,
        }}>
          <Icon name="target" size={16} color={UI.text.muted}/>
          <span style={{ flex: 1, minWidth: 180, fontSize: 14, color: UI.text.secondary, lineHeight: 1.5 }}>
            {pending.length === 1
              ? <>החזקה אחת עוד בלי תחום מסומן · סמן תחום למעקב</>
              : <><LTR>{pending.length}</LTR> החזקות עוד בלי תחום מסומן · סמן תחום למעקב בכל אחת</>}
          </span>
          <button onClick={start} style={{ ...BTN.secondary, padding: "8px 14px", minHeight: 40, fontSize: 14 }}>
            סמן עכשיו
          </button>
          <button onClick={() => { writeDismissed(); setDismissed(true); }} aria-label="לא עכשיו · יוצג שוב בעוד 30 יום"
            title="לא עכשיו · יוצג שוב בעוד 30 יום"
            style={{ background: "transparent", border: "none", padding: 8, cursor: "pointer", display: "flex", alignItems: "center", color: UI.text.muted }}>
            <Icon name="x" size={16} color={UI.text.muted}/>
          </button>
        </div>
      )}

      {queue && queue.length > 0 && (
        <SegmentPicker key={queue[0]} symbol={queue[0]} onDone={next} onClose={stop}/>
      )}
    </>
  );
}
